/// <amd-module name="TFS/Token/PatTokenPage/Actions/TokenActionCreator" />
import { ServiceInstanceTypes } from "Constants/TokenConstants";
import {
  CreatedByOptions,
  DisplayFilterOptions,
  SessionToken,
  SortByOptions
} from "Contracts/Token";
import { TokenConfig } from "Models/TokenConfig";
import { Token } from "Models/Token";
import { ITokenRestClient } from "RestClient/Token";
import * as Resources from "TFS/Token/PatTokenPage/Resources";
import { format } from "VSS/Core/Util/String";
import { IOrganization } from "VSS/Features/Services/OrganizationsService";
import { ITokenProviderService } from "VSS/Platform/TokenProvider";
import { IVssPageContext, IVssRestClientOptions, TeamFoundationHostType } from "VSS/Platform/Context";
import { IServerException, IVssContributionService } from "VSS/Platform/Contribution";
import { Action } from "VSS/Reflux/Action";
import { ILocationsRestClient } from "VSS/Platform/RestClient/Locations";
import { getServiceLocation } from "VSS/Platform/Location";

export interface TokenFilter {
  status: DisplayFilterOptions;
  organization: IOrganization | undefined;
}

export class TokenDispatcherHub {
  public startedTokenFetch = new Action<{
    fetchVersion: number;
    filter: TokenFilter;
    sortBy: SortByOptions;
    isDescending: boolean;
  }>();
  public addedToken = new Action<{ token: Token }>();
  public updatedToken = new Action<{ token: Token }>();
  public revokedToken = new Action<{ authorizationId: string }>();
  public recievedNewPageOfTokens = new Action<{
    fetchVersion: number;
    tokens: Token[];
    nextRowNumber: number;
    numberTokensBeforeFilter: number;
  }>();
  public finishedTokenFetch = new Action<{
    fetchVersion: number;
    tokens: Token[];
    nextRowNumber: number;
    numberTokensBeforeFilter: number;
    filter: TokenFilter;
  }>();
  public receivedErrorFromServer = new Action<{ errorMessage: string }>();
}

export class TokenActionCreator {
  public static getInstance(
    pageContext: IVssPageContext,
    tokenConfig?: TokenConfig
  ): TokenActionCreator {
    if (!TokenActionCreator._instance) {
      TokenActionCreator._instance = new TokenActionCreator(
        pageContext,
        tokenConfig
      );
    }
    return TokenActionCreator._instance;
  }

  private static _instance: TokenActionCreator;
  public dispatchers: TokenDispatcherHub = new TokenDispatcherHub();
  private pageContext: IVssPageContext;
  private tokenConfig: TokenConfig;
  private readonly issueSessionTokenDataProviderContributionId =
    "ms.vss-token-web.personal-access-token-issue-session-token-provider";

  private fetchVersion = 0;
  private previousFilter: TokenFilter = { status: DisplayFilterOptions.Active, organization: undefined };
  private previousSortBy: SortByOptions = SortByOptions.DisplayDate;
  private previousIsDescending: boolean = true;
  private skip = 1;
  private isHosted: boolean;

  constructor(pageContext: IVssPageContext, tokenConfig?: TokenConfig) {
    this.pageContext = pageContext;
    this.tokenConfig = tokenConfig || TokenConfig.getInstance(pageContext);
    this.isHosted = !!this.tokenConfig.hostPageData?.isHosted;
  }

  public addToken = async (newToken: Token): Promise<void> => {
    const addedToken = await this.issueSessionToken(newToken);
    if (addedToken) {
      this.dispatchers.addedToken.invoke({ token: addedToken });
      // Server side is too slow to include tokens. this should summon the confirm dialog anyways so users won't notice
      await this.delay(1000);
      this.resetTokens({ status: DisplayFilterOptions.Active, organization: this.previousFilter.organization });
    }
  };

  public updateToken = async (updatedToken: Token): Promise<void> => {
    const addedToken = await this.issueSessionToken(updatedToken);
    if (addedToken) {
      this.dispatchers.updatedToken.invoke({ token: addedToken });
      this.resetTokens({ status: DisplayFilterOptions.Active, organization: this.previousFilter.organization });
    }
  };

  private issueSessionToken = async (
    token: Token
  ): Promise<Token | undefined> => {
    const vssContributionService = this.pageContext.getService<
      IVssContributionService
    >("IVssContributionService");
    let addResult: SessionToken | undefined;
    try {
      addResult = await vssContributionService.getDataAsync<SessionToken>(
        this.issueSessionTokenDataProviderContributionId,
        token.toSessionToken(),
        true
      );
      if (!addResult) {
        let dataProviderException = vssContributionService.getDataProviderExceptions() ?
          vssContributionService.getDataProviderExceptions()[this.issueSessionTokenDataProviderContributionId] : undefined;
        let errorMessage = this.getErrorMessageFromException(dataProviderException);
        this.dispatchers.receivedErrorFromServer.invoke({
          errorMessage: errorMessage
        });
      }
      else {
        this.dispatchers.receivedErrorFromServer.invoke({ errorMessage: "" });
      }
    } catch (ex) {
      this.dispatchers.receivedErrorFromServer.invoke({ errorMessage: ex.message });
    }

    return addResult && new Token(addResult);
  };

  public revokeToken = async (authorizationId: string): Promise<void> => {
    if (!authorizationId || authorizationId.trim() == '') {
      alert("No token selected!");
      throw new Error("null or empty string passed to revokeToken");
    }

    const restClient = this.pageContext.getRestClient<ITokenRestClient>(
      "ITokenRestClient",
      await this.getRestClientOptions(this.previousFilter.organization)
    );
    await restClient.revokeSessionToken(authorizationId);
    this.dispatchers.revokedToken.invoke({ authorizationId });
    this.resetTokens();
  };

  public getToken = async (
    authorizationId: string
  ): Promise<Token | undefined> => {
    const restClient = this.pageContext.getRestClient<ITokenRestClient>(
      "ITokenRestClient",
      await this.getRestClientOptions(this.previousFilter.organization)
    );
    let token: SessionToken | undefined;
    try {
      token = await restClient.getSessionToken(authorizationId);
      if (!token) {
        this.dispatchers.receivedErrorFromServer.invoke({
          errorMessage: Resources.FailedToRetrieveSessionToken
        });
      }
    } catch (ex) {
      this.dispatchers.receivedErrorFromServer.invoke({ errorMessage: ex.message });
    }

    return token && new Token(token);
  };

  public resetTokens = async (
    filter: TokenFilter = this.previousFilter,
    sortBy: SortByOptions = this.previousSortBy,
    isDescending: boolean = this.previousIsDescending
  ): Promise<void> => {
    const { getTokenPageSize } = this.tokenConfig.tokenPageData;
    const restClient = this.pageContext.getRestClient<ITokenRestClient>(
      "ITokenRestClient",
      await this.getRestClientOptions(filter.organization)
    );
    const currentDate = new Date().toUTCString();
    this.skip = 1;
    const newFetchVersion = ++this.fetchVersion;
    this.dispatchers.startedTokenFetch.invoke({
      fetchVersion: newFetchVersion,
      filter,
      sortBy,
      isDescending
    });
    this.previousFilter = filter;
    this.previousSortBy = sortBy;
    this.previousIsDescending = isDescending;

    let pagedSessionTokens = await restClient.getSessionTokensPage(
      filter.status,
      CreatedByOptions.All,
      sortBy,
      isDescending,
      1,
      getTokenPageSize,
      currentDate
    );
    const tokens = pagedSessionTokens.sessionTokens.map(token => {
      return new Token(token);
    });

    if (newFetchVersion === this.fetchVersion) {
      this.skip += pagedSessionTokens.sessionTokens.length;
    }

    // TODO: Filtering on client side should be cleaned after the Token Sharding is completed
    let filteredTokens = this.filterTokens(tokens, filter.organization);
    let canLoadMoreTokens: boolean = tokens.length === getTokenPageSize && pagedSessionTokens.nextRowNumber !== 0;

    while (canLoadMoreTokens && filteredTokens.length === 0) {
      pagedSessionTokens = await restClient.getSessionTokensPage(
        filter.status,
        CreatedByOptions.All,
        sortBy,
        isDescending,
        this.skip,
        getTokenPageSize,
        currentDate
      );
      const tokens = pagedSessionTokens.sessionTokens.map(token => new Token(token));

      if (newFetchVersion === this.fetchVersion) {
        this.skip += pagedSessionTokens.sessionTokens.length;
      }

      filteredTokens = this.filterTokens(tokens, filter.organization);
      canLoadMoreTokens = tokens.length === getTokenPageSize && pagedSessionTokens.nextRowNumber !== 0;
    }

    this.dispatchers.finishedTokenFetch.invoke({
      fetchVersion: newFetchVersion,
      tokens: filteredTokens,
      nextRowNumber: pagedSessionTokens.nextRowNumber,
      numberTokensBeforeFilter: tokens.length,
      filter: filter
    });
  };

  private filterTokens = (tokens: Token[], organization: IOrganization | undefined): Token[] => {
    if (this.isHosted && organization) {
      return tokens.filter(token => {
        if (!token.targetAccounts?.length) {
          return true;
        } else {
          return token.targetAccounts.some(account => organization.id === account);
        }
      });
    }
    return tokens;
  }

  public loadNextPageOfTokens = async () => {
    const { getTokenPageSize } = this.tokenConfig.tokenPageData;
    const restClient = this.pageContext.getRestClient<ITokenRestClient>(
      "ITokenRestClient",
      await this.getRestClientOptions(this.previousFilter.organization)
    );
    const currentDate = new Date().toUTCString();
    const currentFetchVersion = this.fetchVersion;
    let pagedSessionTokens = await restClient.getSessionTokensPage(
      this.previousFilter.status,
      CreatedByOptions.All,
      this.previousSortBy,
      this.previousIsDescending,
      this.skip,
      getTokenPageSize,
      currentDate
    );
    const tokens = pagedSessionTokens.sessionTokens.map(token => {
      return new Token(token);
    });

    // TODO: Filtering on client side should be cleaned after the Token Sharding is completed
    let filteredTokens = this.filterTokens(tokens, this.previousFilter.organization);
    let canLoadMoreTokens: boolean = tokens.length === getTokenPageSize && pagedSessionTokens.nextRowNumber !== 0;

    while (canLoadMoreTokens && filteredTokens.length === 0) {
      pagedSessionTokens = await restClient.getSessionTokensPage(
        this.previousFilter.status,
        CreatedByOptions.All,
        this.previousSortBy,
        this.previousIsDescending,
        this.skip,
        getTokenPageSize,
        currentDate
      );
      const tokens = pagedSessionTokens.sessionTokens.map(token => new Token(token));

      if (currentFetchVersion === this.fetchVersion) {
        this.skip += pagedSessionTokens.sessionTokens.length;
      }

      filteredTokens = this.filterTokens(tokens, this.previousFilter.organization);
      canLoadMoreTokens = tokens.length === getTokenPageSize && pagedSessionTokens.nextRowNumber !== 0;
    }

    if (currentFetchVersion === this.fetchVersion) {
      this.skip += pagedSessionTokens.sessionTokens.length;
    }
    this.dispatchers.recievedNewPageOfTokens.invoke({
      fetchVersion: currentFetchVersion,
      tokens: filteredTokens,
      nextRowNumber: pagedSessionTokens.nextRowNumber,
      numberTokensBeforeFilter: tokens.length
    });
  };


  /** Gets the IVssRestClientOptions to pass to getRestClient for authenticating against an external organization. */
  private async getRestClientOptions(organization: IOrganization | undefined): Promise<IVssRestClientOptions | undefined> {
    const tokenService = this.pageContext.getService<ITokenProviderService>("ITokenProviderService");
    const tokenProvider = tokenService.getDefaultProvider();

    // Project Collection (organization) Level Calls to Token only happens if a token provider is present
    if (this.isHosted && organization && tokenProvider) {
      // Get the SPS deployment URL
      const spsDeploymentUrl = getServiceLocation(this.pageContext, ServiceInstanceTypes.SPS, TeamFoundationHostType.Deployment);

      // Get the SPS host URL from the location service
      const locationsClient = this.pageContext.getRestClient<ILocationsRestClient>(
        "ILocationsRestClient",
        {rootPath: spsDeploymentUrl}
      );
      const organizationResourceInfo = await locationsClient.getResourceArea(ServiceInstanceTypes.SPS, undefined, organization.name);

      return {
        authTokenProvider: tokenProvider,
        rootPath: organizationResourceInfo?.locationUrl || "",
        serviceInstanceType: ServiceInstanceTypes.SPS,
        hostType: TeamFoundationHostType.ProjectCollection,
      };
    }
  }

  private async delay(timeout: number): Promise<void> {
    await new Promise(resolve => {
      window.setTimeout(resolve, timeout);
    });
  }

  private getErrorMessageFromException(serverException?: IServerException): string {
    if (serverException === undefined) {
      return Resources.FailedToAddSessionToken;
    }

    switch (serverException.message) {
      case "GlobalPatPolicyViolation": {
        return format(Resources.PatPoliciesChangedWarningFormat, Resources.GlobalPat);
      }
      case "FullScopePatPolicyViolation": {
        return format(Resources.PatPoliciesChangedWarningFormat, Resources.FullScopedPat);
      }
      case "PatLifespanPolicyViolation": {
        return format(Resources.PatPoliciesChangedWarningFormat, "", Resources.PatMaxLifespanGenericError);
      }
      case "InvalidDisplayName":
      case "Invalid Token: InvalidDisplayName": {
        return Resources.InvalidDisplayName;
      }
      default: {
        return Resources.FailedToAddSessionToken;
      }
    }
  }
}
